import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  IconButton, 
  Chip,
  TextField,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import BookIcon from '@mui/icons-material/Book';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Components } from 'react-markdown';
import { StoryDisplayProps } from '../../types/lessonPlan.types';

// Removes "Título:" or "Title:" prefix from a title string
const stripTitlePrefix = (title: string): string => {
  if (!title) return '';
  const prefixRegex = /^\s*(?:title|t[íi]tulo)\s*:\s*/i;
  return title.replace(prefixRegex, '').trim();
};

// Removes leading title line(s) like "Título: ..." or "Title: ..." from the story content
const stripLeadingTitleLine = (text: string): string => {
  if (!text) return text;
  let result = text.trimStart();
  const titleLineRegex = /^(?:\s*(?:<p>)?\s*(?:title|t[íi]tulo)\s*:\s*.*?(?:<\/p>)?\s*)(?:\r?\n)?/i;
  while (titleLineRegex.test(result)) {
    result = result.replace(titleLineRegex, '').trimStart();
  }
  return result;
};

// Normalize text for safe comparisons (lowercase, remove accents/punctuation, collapse spaces)
const normalizeForComparison = (value: string): string => {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Fix LaTeX math notation - handle various LaTeX patterns for proper rendering
// Converts to $...$ format for inline math and $$...$$ for block math (remark-math standard)
const fixLatexNotation = (text: string): string => {
  if (!text) return text;
  let fixed = text;
  
  // Step 0: Fix malformed LaTeX expressions (mixed delimiters, incomplete expressions)
  // Fix patterns like: $$...$ or $...$$ or $$...$$$ (extra delimiters)
  fixed = fixed.replace(/\$\$([^$]*)\$\$\$/g, '$$$1$$'); // $$...$$$ -> $$...$$
  fixed = fixed.replace(/\$\$([^$]*)\$/g, '$$$1$$'); // $$...$ -> $$...$$
  fixed = fixed.replace(/\$([^$]*)\$\$/g, '$$$1$$'); // $...$$ -> $$...$$
  
  // Fix incomplete expressions like: $ \frac{3}{1} \times = $ (missing right side)
  fixed = fixed.replace(/\$\s*([^$]*)\s*=\s*\$/g, (match, left) => {
    // If there's an incomplete expression, try to fix it
    if (left.includes('\\times') && !left.includes('=')) {
      return `$${left.trim()}$`; // Just keep the left side for now
    }
    return match;
  });
  
  // Step 1: Convert block math \[ ... \] to $$ ... $$ (remark-math format)
  fixed = fixed.replace(/\\\[([^\]]*)\\\]/g, (match, content) => {
    return `$$${content.trim()}$$`;
  });
  
  // Also handle [ \ ... ] -> $$ ... $$
  fixed = fixed.replace(/\[\s*\\([^\]]*)\\\s*\]/g, (match, content) => {
    return `$$${content.trim()}$$`;
  });
  
  // Step 2: Convert inline math \( ... \) to $ ... $ (remark-math format)
  // This is the standard format that remark-math expects
  fixed = fixed.replace(/\\\(([^)]*)\\\)/g, (match, content) => {
    return `$${content.trim()}$`;
  });
  
  // Step 3: Fix inline math: Convert parentheses notation with LaTeX commands to $ ... $
  // Pattern: ( \frac{...} ... ) or ( \div ... ) etc. -> $ ... $
  // Only convert if it contains LaTeX commands (frac, div, times, sqrt, etc.)
  fixed = fixed.replace(/\(([^)]*(?:\\frac|\\div|\\times|\\sqrt|\\pm|\\leq|\\geq|\\neq|\\approx|\\sum|\\prod|\\int|\\partial)[^)]*)\)/g, (match, content) => {
    // Check if it's already properly delimited with $ or $$
    if (match.startsWith('$') || match.startsWith('\\(')) {
      return match;
    }
    return `$${content.trim()}$`;
  });
  
  // Step 4: Clean up any remaining malformed patterns
  // Remove standalone $ or $$ without content (but preserve valid delimiters)
  fixed = fixed.replace(/\$\s+\$/g, ' '); // $ $ -> (space)
  fixed = fixed.replace(/\$\$\s+\$\$/g, ' '); // $$ $$ -> (space)
  
  return fixed;
};

// Helper to safely render children from ReactMarkdown (handles strings, arrays, objects)
const safeRenderChildren = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string' || typeof children === 'number') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map((child, idx) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return child;
      }
      if (React.isValidElement(child)) {
        return <React.Fragment key={idx}>{child}</React.Fragment>;
      }
      // Handle objects that might be passed
      if (child && typeof child === 'object' && child !== null && !Array.isArray(child)) {
        // Try to extract meaningful content - use type guard for safety
        const childObj = child as unknown as Record<string, unknown>;
        if ('props' in childObj && childObj.props && typeof childObj.props === 'object' && childObj.props !== null) {
          const props = childObj.props as Record<string, unknown>;
          if ('children' in props) {
            return <React.Fragment key={idx}>{safeRenderChildren(props.children as React.ReactNode)}</React.Fragment>;
          }
        }
        return null;
      }
      return null;
    });
  }
  if (React.isValidElement(children)) {
    return children;
  }
  // If it's an object, try to extract content or return null
  if (children && typeof children === 'object' && children !== null && !Array.isArray(children)) {
    const childrenObj = children as unknown as Record<string, unknown>;
    if ('props' in childrenObj && childrenObj.props && typeof childrenObj.props === 'object' && childrenObj.props !== null) {
      const props = childrenObj.props as Record<string, unknown>;
      if ('children' in props) {
        return safeRenderChildren(props.children as React.ReactNode);
      }
    }
    return null;
  }
  return children;
};

// Removes a first content line if it duplicates the title (with or without markdown heading)
const stripDuplicateTitleLine = (text: string, title: string): string => {
  if (!text) return text;
  const withoutPrefixed = stripLeadingTitleLine(text);
  const lines = withoutPrefixed.split('\n');

  let firstIndex = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].trim()) {
      firstIndex = i;
      break;
    }
  }
  if (firstIndex === -1) return withoutPrefixed; // all blank

  const firstLineRaw = lines[firstIndex].replace(/^\s*#+\s*/, '').trim(); // strip markdown heading hashes
  const normalizedFirst = normalizeForComparison(firstLineRaw);
  const normalizedTitle = normalizeForComparison(title);

  if (normalizedFirst && normalizedTitle && normalizedFirst === normalizedTitle) {
    // remove this line and the following blank line if present
    const remaining = lines.slice(firstIndex + 1);
    while (remaining.length && remaining[0].trim() === '') {
      remaining.shift();
    }
    // keep any leading blank lines prior to firstIndex (rare)
    const prefix = lines.slice(0, firstIndex).filter(l => l.trim() !== '');
    const reconstructed = (prefix.length ? prefix.join('\n') + '\n' : '') + remaining.join('\n');
    return reconstructed.trimStart();
  }

  return withoutPrefixed;
};

// Custom markdown components for consistent styling
const markdownComponents: Partial<Components> = {
  h1: ({ children }) => (
    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main', fontSize: '1.1rem' }}>
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography variant="subtitle2" component="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary', fontSize: '1rem' }}>
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography variant="body1" component="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary', fontSize: '0.95rem' }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => {
    // Safely convert children to string for drama content detection
    const safeChildren = safeRenderChildren(children);
    const content = typeof safeChildren === 'string' 
      ? safeChildren 
      : Array.isArray(safeChildren)
        ? safeChildren.map(c => typeof c === 'string' ? c : '').join('')
        : String(safeChildren || '');
    
    // Check if this is drama content by looking for bold character names pattern (improved for multi-word names)
    const isDramaLine = /\*\*[A-Z][^*]*\*\*:/ .test(content) || 
                        /[A-Z][^:]*\*\*:/ .test(content);
    
    if (isDramaLine || content.includes('**')) {
      // For drama lines, process bold formatting manually and preserve the line structure
      // Handle both **CHARACTER**: and CHARACTER**: patterns
      const processedContent = content
        .replace(/\*\*([^*]+)\*\*:/g, '<strong>$1</strong>:')
        .replace(/([A-Z][A-Z\s.]+)\*\*:/g, '<strong>$1</strong>:')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      return (
        <Typography 
          variant="body1" 
          component="div"
          sx={{ 
            lineHeight: 1.6,
            textAlign: 'left',
            mb: 0.5,
            whiteSpace: 'pre-wrap'
          }}
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      );
    }
    
    // For regular paragraphs, use normal paragraph formatting with safe children rendering
    return (
      <Typography 
        variant="body1" 
        component="p"
        paragraph 
        sx={{ 
          lineHeight: 1.8,
          textAlign: 'justify',
          mb: 2
        }}
      >
        {safeChildren}
      </Typography>
    );
  },
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, mb: 2 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.5, lineHeight: 1.6 }}>
      {safeRenderChildren(children)}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box 
      component="blockquote"
      sx={{ 
        borderLeft: '4px solid',
        borderColor: 'primary.light',
        pl: 2,
        py: 1,
        mb: 2,
        backgroundColor: 'grey.50',
        fontStyle: 'italic'
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Divider sx={{ my: 2 }} />
  ),
  code: ({ className, children }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <Box
          component="code"
          sx={{
            backgroundColor: 'grey.200',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.9em',
            fontFamily: 'monospace'
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          display: 'block',
          backgroundColor: 'grey.100',
          p: 2,
          borderRadius: 1,
          fontSize: '0.9em',
          fontFamily: 'monospace',
          overflowX: 'auto',
          my: 2,
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        backgroundColor: 'grey.100',
        p: 2,
        borderRadius: 1,
        fontSize: '0.9em',
        fontFamily: 'monospace',
        overflowX: 'auto',
        my: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0
        }
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box component="div" sx={{ overflowX: 'auto', my: 2 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        {children}
      </Box>
    </Box>
  ),
  th: ({ children }) => (
    <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', py: 1, pr: 2, fontWeight: 600 }}>
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box component="td" sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1, pr: 2 }}>
      {children}
    </Box>
  ),
};

/**
 * StoryDisplay Component
 * 
 * Displays generated stories with markdown formatting and editing capabilities.
 * Shows word count, reading time, and allows inline editing when enabled.
 */
const StoryDisplay: React.FC<StoryDisplayProps> = ({
  story,
  isStreaming,
  editable = false,
  onEdit,
  genre
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  // Handle edit mode toggle
  const handleEditStart = () => {
    if (story) {
      setEditContent(stripLeadingTitleLine(story.content));
      setIsEditing(true);
    }
  };

  // Handle save edit
  const handleSave = () => {
    if (onEdit) {
      onEdit(editContent);
    }
    setIsEditing(false);
  };

  // Handle cancel edit
  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
  };

  // Show placeholder when no story is available
  if (!story && !isStreaming) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          mb: 3, 
          textAlign: 'center',
          backgroundColor: 'grey.50',
          border: '2px dashed',
          borderColor: 'grey.300'
        }}
      >
        <BookIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Click "Generate" to create your passage
        </Typography>
      </Paper>
    );
  }

  // Prepare displayed content (strip prefixed title and exact duplicate of title on first line)
  const displayedContent = story 
    ? fixLatexNotation(stripDuplicateTitleLine(story.content, stripTitlePrefix(story.title)))
    : '';

  return (
    <Paper elevation={1} sx={{ mb: 3 }}>
      {/* Story Content */}
      <Box sx={{ p: 3 }}>
        {/* Title row */}
        {story && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {stripTitlePrefix(story.title) || 'Untitled Story'}
            </Typography>
            {editable && !isEditing && (
              <Button
                onClick={handleEditStart}
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                Edit Story
              </Button>
            )}
          </Box>
        )}

        {isEditing ? (
          // Edit Mode
          <Box>
            <TextField
              multiline
              rows={8}
              fullWidth
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Enter your story content..."
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                size="small"
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          // Display Mode - With ReactMarkdown
          <Box>
            {isStreaming ? (
              <Box sx={{ 
                minHeight: 200, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 1,
                p: 3
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  <em>Generating content...</em>
                </Typography>
              </Box>
            ) : story ? (
              <Box sx={{ '& > *:last-child': { mb: 0 } }}>
                {/* GENRE-SPECIFIC FORMATTING: Use specialized rendering for Drama and Poetry */}
                {typeof displayedContent === 'string' ? 
                  genre === 'Poetry' ?
                    // Poetry content: render with stanza breaks and preserve line structure
                    <Box>
                      {displayedContent.split('\n\n').map((stanza, stanzaIndex) => {
                        if (!stanza.trim()) return null;
                        
                        return (
                          <Box key={stanzaIndex} sx={{ mb: 3 }}>
                            {stanza.split('\n').map((line, lineIndex) => {
                              const trimmedLine = line.trim();
                              if (!trimmedLine) return <Box key={lineIndex} sx={{ height: '0.5em' }} />;
                              
                              return (
                                <Typography 
                                  key={lineIndex} 
                                  variant="body1" 
                                  sx={{ 
                                    lineHeight: 1.6,
                                    textAlign: 'center',
                                    mb: 0.5,
                                    fontStyle: 'italic',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                >
                                  {trimmedLine}
                                </Typography>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  : (displayedContent.includes('**') && displayedContent.includes('(Characters):')) ? 
                    // Drama content: render line-by-line to preserve script formatting
                    <Box>
                      {displayedContent.split('\n').map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return <Box key={index} sx={{ height: '0.5em' }} />;

                        // Handle section headers like "Scene:", "(Characters):", "(Dialogue and Stage Directions):"
                        if (trimmedLine.match(/^(Scene:|.*Characters.*:|.*Dialogue.*:|.*Stage.*:)/)) {
                          return (
                            <Typography 
                              key={index} 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600,
                                mt: index > 0 ? 2 : 0,
                                mb: 1,
                                color: 'primary.main'
                              }}
                            >
                              {trimmedLine}
                            </Typography>
                          );
                        }

                        // Check if this is a character dialogue line (improved pattern for multi-word names)
                        const isDramaLine = /\*\*[A-Z][^*]*\*\*:/ .test(trimmedLine) || 
                                          /[A-Z][^:]*\*\*:/ .test(trimmedLine) || 
                                          trimmedLine.includes('**');
                        
                        if (isDramaLine) {
                          // Process character names and dialogue - handle both **NAME**: and NAME**: patterns
                          let processedContent = trimmedLine
                            .replace(/\*\*([^*]+)\*\*:/g, '<strong>$1</strong>:')  // **CHARACTER**:
                            .replace(/([A-Z][A-Z\s.]+)\*\*:/g, '<strong>$1</strong>:')  // CHARACTER**:
                            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');  // **text**
                          
                          return (
                            <Typography 
                              key={index} 
                              variant="body1" 
                              component="div"
                              sx={{ 
                                lineHeight: 1.4,
                                textAlign: 'left',
                                mb: 0.5,
                                whiteSpace: 'pre-wrap'
                              }}
                              dangerouslySetInnerHTML={{ __html: processedContent }}
                            />
                          );
                        }

                        // Regular lines (stage directions, descriptions)
                        return (
                          <Typography 
                            key={index} 
                            variant="body1" 
                            sx={{ 
                              lineHeight: 1.4,
                              mb: 0.5,
                              textAlign: 'left',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {trimmedLine}
                          </Typography>
                        );
                      })}
                    </Box>
                  :
                    // Non-drama content: use ReactMarkdown with GFM and Math support
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm, remarkMath]} 
                      rehypePlugins={[rehypeKatex]}
                      components={markdownComponents}
                    >
                      {displayedContent}
                    </ReactMarkdown>
                : 'Error: Invalid story content format'}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default StoryDisplay;  